import {ChangeDetectorRef, Component, NgZone, OnInit, ViewChild} from '@angular/core';
import {debounceTime, distinctUntilChanged, Subject, take} from 'rxjs';
import {Scroller} from 'primeng/scroller';
import {CompetentAuthorityModel} from 'src/app/shared/models/competent-authority.model';
import {CompetentAuthoritiesRepository} from 'src/app/shared/states/competent-authorities.repository';
import {ManagementHelperService} from '../../Service/management-helper.service';
import {finalize} from "rxjs/operators";

@Component({
  selector: 'app-competent-authorities-management',
  templateUrl: './competent-authorities-management.component.html',
  styleUrls: ['./competent-authorities-management.component.scss'],
  standalone: false,
})
export class CompetentAuthoritiesManagementComponent implements OnInit {

  @ViewChild(Scroller) scroller!: Scroller;
  groupedAuthorities: Record<string, CompetentAuthorityModel[]> = {};
  alphabet: string[] = [];

  selectedLetter: string = '';
  filteredAuthorities: CompetentAuthorityModel[] = [];
  visibleAuthorities: CompetentAuthorityModel[] = [];
  searchText: string = '';
  private readonly searchSubject = new Subject<string>();
  searchedAuthorities: CompetentAuthorityModel[] = [];

  showCompetentAuthorityDialog: boolean = false;
  newAuthority: CompetentAuthorityModel = {} as CompetentAuthorityModel;
  highlightedAuthorityId: number | null = null;
  openEditModal: boolean = false;

  isLoading: boolean = false;

  constructor(
    private readonly _authorityRepo: CompetentAuthoritiesRepository,
    private readonly _mgmtHelper: ManagementHelperService,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.alphabet = this._mgmtHelper.getAlphabetWithSpecial();

    this._authorityRepo.getCompetentAuthorities().pipe(
      take(1),
      finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      })
    ).subscribe((authorities: CompetentAuthorityModel[]) => {
      this.groupedAuthorities = this._mgmtHelper.groupByInitial(authorities, 'name');
      // Wähle den ersten Buchstaben mit Einträgen, sonst alphabet[0]
      const firstLetterWithAuthorities = this.alphabet.find(l => this.groupedAuthorities[l] && this.groupedAuthorities[l].length > 0) || this.alphabet[0];
      this.filterByLetter(firstLetterWithAuthorities);
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (searchTerm.length >= 2) {
        this._authorityRepo.searchCompetentAuthorities(searchTerm).pipe(
          take(1),
          finalize(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
          })
        ).subscribe((results: CompetentAuthorityModel[]) => {
          this.searchedAuthorities = results;
          this.visibleAuthorities = [...this.searchedAuthorities];
          this.cdr.detectChanges();
          this.scrollToTop();
        });
      } else {
        this.searchedAuthorities = [];
        this.filterByLetter(this.selectedLetter || this.alphabet[0]);
        this.cdr.detectChanges();
      }
    });
  }

  filterByLetter(letter: string) {
    this.selectedLetter = letter;
    this.filteredAuthorities = this.groupedAuthorities[letter] || [];
    this.visibleAuthorities = [...this.filteredAuthorities];
    this.cdr.detectChanges();
    this.scrollToTop();
  }

  private scrollToTop(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.scroller?.scrollToIndex(0);
      });
    });
  }

  onSearchChange(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchText = inputValue;
    this.searchSubject.next(inputValue);
  }

  clearSearch() {
    this.searchText = '';
    this.searchedAuthorities = [];
    this.searchSubject.next('');
    this.filterByLetter(this.selectedLetter || this.alphabet[0]);
    this.cdr.detectChanges();
  }

  toggleCreateModal(show: boolean) {
    this.showCompetentAuthorityDialog = show;
  }

  toggleEditModal(show: boolean, authority?: CompetentAuthorityModel) {
    if (authority) {
      this.newAuthority = {...authority};
    }
    this.openEditModal = show;
  }

  onCompetentAuthorityCreated(newAuthority: CompetentAuthorityModel): void {
    const firstLetter = this.getGroupKey(newAuthority.name ?? '');

    if (!this.groupedAuthorities[firstLetter]) {
      this.groupedAuthorities[firstLetter] = [];
    }

    this.groupedAuthorities[firstLetter] = [
      ...this.groupedAuthorities[firstLetter],
      newAuthority
    ].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    this.filterByLetter(firstLetter);

    setTimeout(() => {
      this.highlightedAuthorityId = newAuthority.id;
      this.cdr.detectChanges();

      const index = this.filteredAuthorities.findIndex(a => a.id === newAuthority.id);
      if (index !== -1 && this.scroller) {
        this.scroller.scrollToIndex(index);
      }

      setTimeout(() => {
        this._mgmtHelper.scrollToElementById(`authority-${newAuthority.id}`);
        setTimeout(() => {
          this.highlightedAuthorityId = null;
          this.cdr.detectChanges();
        }, 2000);
      }, 300);
    }, 100);
  }

  onCompetentAuthorityUpdated(updatedAuthority: CompetentAuthorityModel): void {
    const newKey = this.getGroupKey(updatedAuthority.name ?? '');

    let oldKey: string | null = null;
    for (const key in this.groupedAuthorities) {
      if (this.groupedAuthorities[key].some(a => a.id === updatedAuthority.id)) {
        oldKey = key;
        break;
      }
    }

    if (oldKey && oldKey !== newKey) {
      this.groupedAuthorities[oldKey] = this.groupedAuthorities[oldKey].filter(a => a.id !== updatedAuthority.id);
    }

    if (!this.groupedAuthorities[newKey]) {
      this.groupedAuthorities[newKey] = [];
    }

    this.groupedAuthorities[newKey] = [
      ...this.groupedAuthorities[newKey].filter(a => a.id !== updatedAuthority.id),
      updatedAuthority
    ].sort((a, b) => (a.name?.toLowerCase() || '').localeCompare(b.name?.toLowerCase() || ''));

    this.openEditModal = false;
    this.filterByLetter(newKey);
    this.cdr.detectChanges();
  }

  trackByAuthority(index: number, authority: CompetentAuthorityModel): number {
    return authority.id!;
  }

  formatAddress(contact: any): string {
    if (!contact) {
      return '-';
    }
    const street = contact.street?.trim();
    const zipCity = [contact.zipCode?.trim(), contact.city?.trim()].filter(Boolean).join(' ');
    const address = [street, zipCity].filter(Boolean).join(', ');
    return address || '-';
  }

  private getGroupKey(value: string): string {
    const first = (value || '').trim().charAt(0).toUpperCase() || '#';
    if (first === '#') return '#';
    return /^[A-Z]$/.test(first) ? first : '?';
  }
}
